import Header from "@/components/navigation/Header";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Calendar,
  MessageSquare,
  Users,
  Shield
} from "lucide-react";

const Contact = () => {
  const contactMethods = [
    {
      title: "Email Us",
      description: "Get a response within 24 hours",
      icon: Mail,
      value: "info@chiefbitcoinofficer.com",
      action: "mailto:info@chiefbitcoinofficer.com"
    },
    {
      title: "Call Us",
      description: "Speak directly with our team",
      icon: Phone,
      value: "+1 (555) 123-4567",
      action: "tel:+15551234567"
    },
    {
      title: "Office Hours",
      description: "Monday - Friday, 9AM - 5PM PST",
      icon: Clock,
      value: "Business Hours",
      action: null
    }
  ];

  const services = [
    "Investment Strategy Consultation",
    "Bitcoin Custody Setup",
    "Investment Policy Development",
    "Security Assessment",
    "Financial Reporting Setup",
    "Educational Workshops"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-2xl">
                <MessageSquare className="h-12 w-12 text-bitcoin-orange" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get Started Today
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Ready to integrate Bitcoin into your real estate investment strategy? 
              Our experts are here to help you navigate the digital asset landscape with confidence.
            </p>
          </div>
        </div>
      </section>

      <main className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <Card className="shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">
                    Schedule Your Consultation
                  </CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you within 24 hours 
                    to discuss your Bitcoin and real estate investment needs.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" placeholder="John" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input id="lastName" placeholder="Doe" required />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" type="email" placeholder="john@example.com" required />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" type="tel" placeholder="+1 (555) 123-4567" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company">Company/Organization</Label>
                    <Input id="company" placeholder="Your Company Name" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="services">Services Interested In</Label>
                    <select className="w-full p-3 border border-input rounded-md bg-background">
                      <option value="">Select a service...</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="assets">Current Real Estate Portfolio Size</Label>
                    <select className="w-full p-3 border border-input rounded-md bg-background">
                      <option value="">Select range...</option>
                      <option value="under-1m">Under $1M</option>
                      <option value="1m-5m">$1M - $5M</option>
                      <option value="5m-10m">$5M - $10M</option>
                      <option value="10m-25m">$10M - $25M</option>
                      <option value="25m-50m">$25M - $50M</option>
                      <option value="over-50m">Over $50M</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Tell us about your goals and how we can help..."
                      rows={4}
                    />
                  </div>
                  
                  <Button variant="bitcoin" size="lg" className="w-full">
                    Schedule Consultation
                  </Button>
                  
                  <p className="text-sm text-muted-foreground text-center">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Methods */}
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Contact Information
                </h2>
                <div className="space-y-4">
                  {contactMethods.map((method, index) => {
                    const IconComponent = method.icon;
                    return (
                      <Card key={index} className="hover:shadow-soft transition-smooth">
                        <CardContent className="p-6">
                          <div className="flex items-center space-x-4">
                            <div className="p-3 bg-bitcoin-orange/10 rounded-lg">
                              <IconComponent className="h-6 w-6 text-bitcoin-orange" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">
                                {method.title}
                              </h3>
                              <p className="text-sm text-muted-foreground mb-1">
                                {method.description}
                              </p>
                              {method.action ? (
                                <a 
                                  href={method.action}
                                  className="text-bitcoin-orange hover:underline font-medium"
                                >
                                  {method.value}
                                </a>
                              ) : (
                                <span className="text-foreground font-medium">
                                  {method.value}
                                </span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Why Choose Us */}
              <Card className="bg-muted">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-bitcoin-orange" />
                    <span>Why Choose Chief Bitcoin Officer?</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-bitcoin-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-foreground">Real Estate Focused</h4>
                      <p className="text-sm text-muted-foreground">
                        We understand the unique needs of real estate investors
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-bitcoin-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-foreground">Proven Track Record</h4>
                      <p className="text-sm text-muted-foreground">
                        500+ successful implementations and $50M+ Bitcoin secured
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-bitcoin-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-foreground">Education First</h4>
                      <p className="text-sm text-muted-foreground">
                        We empower you with knowledge, not just solutions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-bitcoin-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-foreground">Security Obsessed</h4>
                      <p className="text-sm text-muted-foreground">
                        Institutional-grade security for all Bitcoin holdings
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Service Areas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-bitcoin-orange" />
                    <span>Service Areas</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    We serve real estate investors and professionals nationwide with:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-bitcoin-orange rounded-full"></div>
                      <span>Remote consultations and strategy sessions</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-bitcoin-orange rounded-full"></div>
                      <span>On-site implementations for large portfolios</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-bitcoin-orange rounded-full"></div>
                      <span>Virtual training and educational workshops</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;